import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { MdAdd, MdDragIndicator, MdClose } from 'react-icons/md';

import { GridColumn, GridView } from './model';

type Props<T> = {
  columns: GridColumn<T>[];
  view: GridView;
  onChange: (view: GridView) => void;
  disabled: boolean;
};

function FieldRow({
  id,
  label,
  index,
  disabled,
  removable,
  onRemove,
}: {
  id: string;
  label: string;
  index: number;
  disabled: boolean;
  removable: boolean;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <Flex
      as="li"
      ref={setNodeRef}
      align="center"
      gap={3}
      bg={isDragging ? 'blue.50' : 'white'}
      borderWidth="1px"
      borderColor={isDragging ? 'blue.300' : 'gray.200'}
      rounded="md"
      px={3}
      py={2}
      position="relative"
      zIndex={isDragging ? 1 : undefined}
      boxShadow={isDragging ? 'lg' : 'none'}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <IconButton
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Переместить поле: ${label}`}
        icon={<MdDragIndicator size={20} />}
        size="sm"
        variant="ghost"
        color="gray.500"
        isDisabled={disabled}
        cursor={isDragging ? 'grabbing' : 'grab'}
        sx={{ touchAction: 'none' }}
      />
      <Text fontSize="xs" color="gray.500" minW={5}>
        {index + 1}
      </Text>
      <Text fontSize="sm" flex={1}>
        {label}
      </Text>
      <Tooltip
        label={
          removable
            ? 'Убрать поле из представления'
            : 'Оставьте хотя бы одно поле'
        }
      >
        <IconButton
          aria-label={`Убрать поле: ${label}`}
          icon={<MdClose />}
          size="sm"
          variant="ghost"
          color="gray.500"
          isDisabled={disabled || !removable}
          onClick={onRemove}
        />
      </Tooltip>
    </Flex>
  );
}

export function ViewFields<T>({ columns, view, onChange, disabled }: Props<T>) {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ordered = view.order
    .map((id) => columns.find((c) => c.id === id))
    .filter(Boolean);
  const visible = ordered.filter((c) => !view.hidden.includes(c.id));
  const available = ordered.filter((c) => view.hidden.includes(c.id));
  const matches = available.filter((c) =>
    c.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const reorder = ({ active, over }: DragEndEvent) => {
    if (disabled || !over || active.id === over.id) {
      return;
    }
    const ids = visible.map((c) => c.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) {
      return;
    }
    onChange({
      ...view,
      order: [...arrayMove(ids, from, to), ...available.map((c) => c.id)],
    });
  };
  const add = (id: string) => {
    if (disabled || !view.hidden.includes(id)) {
      return;
    }
    onChange({
      ...view,
      order: [
        ...visible.map((c) => c.id),
        id,
        ...available.filter((c) => c.id !== id).map((c) => c.id),
      ],
      hidden: view.hidden.filter((item) => item !== id),
    });
  };
  return (
    <Box pb={20}>
      <Flex align="baseline" justify="space-between" mb={2}>
        <Text fontWeight="semibold">Поля представления</Text>
        <Text fontSize="sm" color="gray.500">
          {visible.length} из {columns.length}
        </Text>
      </Flex>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Перетащите поле за значок слева. С клавиатуры: пробел, стрелки, пробел
        для завершения или Escape для отмены.
      </Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={reorder}
        accessibility={{
          screenReaderInstructions: {
            draggable:
              'Нажмите пробел, чтобы взять поле. Стрелки вверх и вниз меняют порядок. Пробел завершает перемещение, Escape отменяет.',
          },
        }}
      >
        <SortableContext
          items={visible.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <Flex
            as="ol"
            aria-label="Поля представления"
            direction="column"
            gap={2}
            listStyleType="none"
            m={0}
            p={0}
          >
            {visible.map((column, index) => (
              <FieldRow
                key={column.id}
                id={column.id}
                label={column.label}
                index={index}
                disabled={disabled}
                removable={visible.length > 1}
                onRemove={() => {
                  if (!disabled && visible.length > 1) {
                    onChange({ ...view, hidden: [...view.hidden, column.id] });
                  }
                }}
              />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>
      <Box position="absolute" bottom={6} right={6} zIndex={2}>
        <Popover
          isOpen={adding && !disabled}
          onOpen={() => {
            setAdding(true);
            setSearch('');
          }}
          onClose={() => setAdding(false)}
          placement="top-end"
          initialFocusRef={inputRef}
        >
          <PopoverTrigger>
            <IconButton
              aria-label="Добавить поле"
              title={
                available.length ? 'Добавить поле' : 'Все поля уже добавлены'
              }
              icon={<MdAdd size={26} />}
              colorScheme="blue"
              rounded="full"
              size="lg"
              boxShadow="lg"
              isDisabled={disabled || !available.length}
            />
          </PopoverTrigger>
          <PopoverContent
            width="340px"
            maxW="calc(100vw - 48px)"
            boxShadow="xl"
          >
            <PopoverCloseButton />
            <PopoverHeader fontWeight="semibold">Добавить поле</PopoverHeader>
            <PopoverBody>
              <Input
                ref={inputRef}
                size="sm"
                placeholder="Поиск по названию…"
                aria-label="Поиск поля"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                mb={2}
              />
              <Box maxH="280px" overflowY="auto">
                {matches.map((column) => (
                  <Button
                    key={column.id}
                    aria-label={`Добавить поле: ${column.label}`}
                    variant="ghost"
                    size="sm"
                    width="100%"
                    justifyContent="space-between"
                    rightIcon={<MdAdd />}
                    onClick={() => add(column.id)}
                  >
                    {column.label}
                  </Button>
                ))}
                {!matches.length && (
                  <Text fontSize="sm" color="gray.500" py={3}>
                    {available.length
                      ? 'Поля не найдены'
                      : 'Все поля добавлены'}
                  </Text>
                )}
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
    </Box>
  );
}
